import moment from 'moment';
import { useState } from 'react';
import { BookingInput, Event, EventStatus } from '../services/Types';
import { createUpdateStaff, eventDetail, updateEventInfo, updateFeature, updateAudience, updateEventtype, updateFullEvent, bookTimeSlots, selectedSlotRoomId, createEvent, submitEvent, approveEventService, seriesEvents, unlockEvent, eventCompleted, transferEventRequester } from '../services/EventService';
import { cancelEvent, revertVersion, sendNotification, deleteEvent } from '../services/WorkflowService';
import { convertMinutesIntoTime } from '../services/Helper';
import { EventType } from '../services/Types';
import { useAllEvents } from './useAllEvents';

// Global fetching state
const fetchingEvents = new Map<number, boolean>();

// Global cache for event details
let eventCache: { [key: number]: Event } = {};

export const useEvent = () => {
  const [errorUseEvent, setErrorUseEvent] = useState<string | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);
  const [logMessage, setLogMessage] = useState('')
  const [eventData, setEventData] = useState<Event | undefined>();
  const { resetAllEventsCache } = useAllEvents();

  const resetEntireCache = () => {
    eventCache = {};
    setEventData(undefined);
  }
  const resetCache = (id: number) => {
    delete eventCache[id];
    setEventData(undefined);
    resetAllEventsCache();
    getEventData(id);
  }

  // Fetch a single event from the database
  const getEventData = async (id: number) => {
    if (id === 0 || id === undefined) {
      return null;
    }

    // Check if event is in cache first
    if (eventCache[id]) {
      setEventData(eventCache[id]);
      setErrorUseEvent(null);
      return eventCache[id];
    }

    // Check if this event is already being fetched
    if (fetchingEvents.get(id)) {
      return null;
    }

    // Mark this event as being fetched
    fetchingEvents.set(id, true);
    setUpdating(true)

    try {
      const { data } = await eventDetail(id);
      if (data) {
        if (data.eventDetail.statusCode === 200) {
          if (data.eventDetail.data === null) {
            throw new Error("Event not found: " + id);
          }
          setEventData(data.eventDetail.data);
          eventCache[id] = data.eventDetail.data;
          setErrorUseEvent(null);
          return data.eventDetail.data;
        } else {
          throw new Error(data.eventDetail.message);
        }
      }
      return null;
    } catch (error) {
      console.error('Error loading event:', error);
      setErrorUseEvent('Error loading event: ' + error);
      return null;
    } finally {
      setUpdating(false)
      // Clear the fetching state for this event
      fetchingEvents.delete(id);
    }
  };

  const getEventRoomBookingFormatted = () => {
    if (!eventData?.roomBooking?.length) return null;
    
    const liveBooking = eventData.roomBooking.find(booking => booking.category === 'live');
    const dryBooking = eventData.roomBooking.find(booking => booking.category === 'dry');
    
    return {
      live: liveBooking ? {
        startTime: convertMinutesIntoTime(liveBooking.startTime),
        endTime: convertMinutesIntoTime(liveBooking.endTime),
        startEndTime: `${convertMinutesIntoTime(liveBooking.startTime)} - ${convertMinutesIntoTime(liveBooking.endTime)}`,
        startDate: moment.utc(Number(liveBooking.date)).format('MMM DD, YYYY'),
        endDate: moment.utc(Number(liveBooking.date)).format('MMM DD, YYYY'),
        startEndDate: moment.utc(Number(liveBooking.date)).format('MMM DD, YYYY'),
        duration: liveBooking.endTime - liveBooking.startTime
      } : null,
      dry: dryBooking ? {
        startTime: convertMinutesIntoTime(dryBooking.startTime),
        endTime: convertMinutesIntoTime(dryBooking.endTime),
        startEndTime: `${convertMinutesIntoTime(dryBooking.startTime)} - ${convertMinutesIntoTime(dryBooking.endTime)}`,
        startDate: moment.utc(Number(dryBooking.date)).format('MMM DD, YYYY'),
        endDate: moment.utc(Number(dryBooking.date)).format('MMM DD, YYYY'),
        startEndDate: moment.utc(Number(dryBooking.date)).format('MMM DD, YYYY'),
        duration: dryBooking.endTime - dryBooking.startTime
      } : null,
      all: eventData.roomBooking.map(booking => ({
        category: booking.category,
        startTime: convertMinutesIntoTime(booking.startTime),
        endTime: convertMinutesIntoTime(booking.endTime),
        startEndTime: `${convertMinutesIntoTime(booking.startTime)} - ${convertMinutesIntoTime(booking.endTime)}`,
        startDate: moment.utc(Number(booking.date)).format('MMM DD, YYYY'),
        endDate: moment.utc(Number(booking.date)).format('MMM DD, YYYY'),
        startEndDate: moment.utc(Number(booking.date)).format('MMM DD, YYYY'),
        duration: booking.endTime - booking.startTime
      }))
    };
  };

  const getEventCreatorDetails = async () => {
    if (!eventData?.createrId) return null;
    // Fetch creator details from User table
    // Cache this separately to avoid refetching
  };

  const emptyEventData = (): Event => {
    return {
      id: 0,
      title: '',
      summary: '',
      description: '',
      creater: { id: 0, firstName: '', lastName: '' },
      createrId: 0,
      staff: [],
      overHundredParticipants: false,
      invitationDistributionList: '',
      sendInvitation: 'OWN',
      invitationNotes: '',
      additionalRequest: '',
      logisticNeed: '',
      publicOrPrivate: 'PUBLIC',
      supportNeeded: '',
      status: EventStatus.SAVED,
      date: '',
      time: 0,
      eventCloseout: null,
      eventType: '',
      otherApproverEmail: '',
      otherApproverName: '',
      roomBooking: [],
      programOfficeId: null,
      levelOneApproverId: null,
      programOffice: null,
      levelOneApprover: null,
      seriesId: 0,
      featuresTranscription: '',
      featuresScript: '',
      tbd_presenters: 0,
      tbd_moderators: 0,
      tbd_supportstaff: 0,
      publicationDate: null,
      updatedAt: new Date(),
      createdAt: new Date()
    }
  };

  // TODO: update useAllEvents when creating or updating an event
  const createDbEvent = async (newEvent: Event, isAdminEvent: boolean) => {
    setUpdating(true)
    setErrorUseEvent(null)
    try {
      const resultsCreateEvent = await createEvent(
        newEvent.title,
        newEvent.summary,
        newEvent.description,
        newEvent.eventType,
        isAdminEvent
      )
      if (resultsCreateEvent.data.createEvent.statusCode !== 200) throw new Error(resultsCreateEvent.data.createEvent.message)

      return resultsCreateEvent.data.createEvent.data.id;
    } catch (error) {
      console.error('Error creating event:', error);
      setErrorUseEvent('Error creating event: ' + error);
      return false;
    } finally {
      setUpdating(false)
    }
  }

  const updateDbEvent = async (newEvent: Event, adminBooking: boolean = false) => {
    setUpdating(true)
    setErrorUseEvent(null)
    console.log("newEvent", newEvent)

    try {
      // updateFullEvent
      const resultsUpdateFullEvent = await updateFullEvent(newEvent)
      if (resultsUpdateFullEvent.data.updateFullEvent.statusCode !== 200) throw new Error(resultsUpdateFullEvent.data.updateFullEvent.message)

      const resultsUpdateBooking = await updateDbBooking(newEvent, adminBooking)
      if (!resultsUpdateBooking) throw new Error("Error updating booking from updateDbEvent")

      // updateStaff
      if (newEvent.staff && newEvent.staff.length >= 0) {
        const resultsUpdateStaff = await createUpdateStaff(newEvent.id, newEvent.staff)
        if (resultsUpdateStaff.data.createUpdateStaff.statusCode !== 200) throw new Error(resultsUpdateStaff.data.createUpdateStaff.message)
      }

      resetCache(newEvent.id)
      return true;
    } catch (error) {
      console.error('Error updating event:', error);
      setErrorUseEvent('Error updating event: ' + error);
      return false;
    } finally {
      setUpdating(false)
    }
  }

  const initSubmitEvent = async (newEvent: Event) => {
    setUpdating(true)
    try {
      const resultsSubmitEvent = await submitEvent(
        Number(newEvent.id)
      )
      if (resultsSubmitEvent.data.submitEvent.statusCode !== 200)
        throw new Error(resultsSubmitEvent.data.submitEvent.message)

      resetCache(newEvent.id)
      return true;
    } finally {
      setUpdating(false)
    }
  }

  const updateDbEventType = async (newEvent: Event) => {
    setUpdating(true)
    try {
      const resultsUpdateEventtype = await updateEventtype(newEvent.id, newEvent.eventType)
      if (resultsUpdateEventtype.data.updateEventtype.statusCode !== 200) throw new Error(resultsUpdateEventtype.data.updateEventtype.message)

      resetCache(newEvent.id)
      return true;
    } catch (error) {
      console.error('Error updating event:', error);
      setErrorUseEvent('Error updating event: ' + error);
      return false;
    } finally {
      setUpdating(false)
    }
  }

  const updateDbEventInfo = async (newEvent: Event) => {
    setUpdating(true)
    try {
      // update event info
      const resultsUpdateEventInfo = await updateEventInfo(
        newEvent.id,
        newEvent.title,
        newEvent.summary,
        newEvent.description
      )
      if (resultsUpdateEventInfo.data.updateEvent.statusCode !== 200) throw new Error(resultsUpdateEventInfo.data.updateEvent.message)

      resetCache(newEvent.id)
        return true;
      } catch (error) {
        console.error('Error updating event:', error);
        setErrorUseEvent('Error updating event: ' + error);
        return false;
      } finally {
        setUpdating(false)
      }
    }

  const updateDbUpdateStaff = async (newEvent: Event) => {
    setUpdating(true)
    try {
      // update staff
      const resultsUpdateStaff = await createUpdateStaff(newEvent.id, newEvent.staff)
      if (resultsUpdateStaff.data.createUpdateStaff.statusCode !== 200) throw new Error(resultsUpdateStaff.data.createUpdateStaff.message)

      resetCache(newEvent.id)
        return true;
      } catch (error) {
        console.error('Error updating event:', error);
        setErrorUseEvent('Error updating event: ' + error);
        return false;
      } finally {
        setUpdating(false)
      }
    }

  const updateDbUpdateFeature = async (newEvent: Event) => {
    setUpdating(true)
    try {
      // update features
      const resultsUpdateFeature = await updateFeature(
        newEvent.id,
        0, //newEvent.programOfficeId,
        0, //newEvent.levelOneApproverId,
        newEvent.otherApproverName,
        newEvent.otherApproverEmail,
        newEvent.publicOrPrivate,
        newEvent.logisticNeed,
        newEvent.supportNeeded,
        newEvent.additionalRequest
      )
      if (resultsUpdateFeature.data.updateFeature.statusCode !== 200) throw new Error(resultsUpdateFeature.data.updateFeature.message)

      resetCache(newEvent.id)
        return true;
      } catch (error) {
        console.error('Error updating event:', error);
        setErrorUseEvent('Error updating event: ' + error);
        return false;
      } finally {
        setUpdating(false)
      }
    }

  const updateDbUpdateAudience = async (newEvent: Event) => {
    setUpdating(true)
    try {
      // update audience
      const resultsUpdateAudience = await updateAudience(
        newEvent.id,
        newEvent.overHundredParticipants,
        newEvent.sendInvitation,
        newEvent.invitationDistributionList,
        '', // newEvent.invitationNotes
      )
      if (resultsUpdateAudience.data.updateAudience.statusCode !== 200) throw new Error(resultsUpdateAudience.data.updateAudience.message)

      resetCache(newEvent.id)
        return true;
      } catch (error) {
        console.error('Error updating event:', error);
        setErrorUseEvent('Error updating event: ' + error);
        return false;
      } finally {
        setUpdating(false)
      }
    }

  const updateDbBooking = async (newEvent: Event, adminBooking: boolean = false) => {
    setUpdating(true)
    if (newEvent.roomBooking.length === 0) {
      return true
    }
    try {
      const liveEvent = newEvent.roomBooking?.[0]
      const dryEvent = newEvent.roomBooking?.[1]

      let liveRoomId = await getSelectedSlotRoomId(
        newEvent,
        'live',
        adminBooking
      )
      if (liveRoomId === 0) {
        setErrorUseEvent('Live Event room not available')
        throw new Error('Live Event room not available')
      }

      let dryRoomId = 0
      if (dryEvent?.startTime) {
        dryRoomId = await getSelectedSlotRoomId(
          newEvent,
          'dry',
          adminBooking
        )
        if (dryRoomId === 0) {
          setErrorUseEvent('Dry Event room not available')
          throw new Error('Dry Event room not available')
        }
      }

      // a const to capture BookingInput
      //TODO change the dates to number instead of converting to strings (once old code removed)
      const bookingInput: BookingInput = {
        liveDuration: liveEvent ? liveEvent.endTime - liveEvent.startTime : 0,
        liveBookingDate: liveEvent ? moment.utc(Number(liveEvent.date)).format('YYYY-MM-DD') : '',
        liveStartTime: liveEvent ? Number(liveEvent.startTime) : 0,
        dryDuration: dryEvent ? (dryEvent.endTime - dryEvent.startTime) : 0,
        dryBookingDate: dryEvent ? moment.utc(Number(dryEvent.date)).format('YYYY-MM-DD') : '',
        dryStartTime: dryEvent ? dryEvent.startTime : 0,
        audience: newEvent.overHundredParticipants ? 3000 : 500,
      }
      const resultsBookTimeSlots = await bookTimeSlots(
        liveRoomId,
        dryRoomId,
        newEvent.id,
        bookingInput
      )
      if (resultsBookTimeSlots.data.bookTimeSlots.statusCode !== 200) throw new Error(resultsBookTimeSlots.data.bookTimeSlots.message)

      resetCache(newEvent.id)
      return true;
    } catch (error) {
      console.error('Error updating event:', error);
      setErrorUseEvent('Error updating event: ' + error);
      throw error;
    } finally {
      setUpdating(false)
    }
  }

  const getSelectedSlotRoomId = async (
    newEvent: Event,
    category: string,
    adminBooking: boolean = false
  ) => {
    // Live event
    const categoryId = category === 'live' ? 0 : 1
    const liveEvent = newEvent.roomBooking?.[0]
    const dryEvent = newEvent.roomBooking?.[1]
    const selectedSlot = convertMinutesIntoTime(newEvent.roomBooking?.[categoryId]?.startTime)
    const duration = newEvent.roomBooking?.[categoryId]?.endTime - newEvent.roomBooking?.[categoryId]?.startTime

    //TODO change the dates to number instead of converting to strings (once old code removed)
    const input: any = {
      duration: duration,
      liveBookingDate: liveEvent ? moment.utc(Number(liveEvent.date)).format('YYYY-MM-DD') : '',
      dryBookingDate: dryEvent ? moment.utc(Number(dryEvent.date)).format('YYYY-MM-DD') : '',
      audience: newEvent.overHundredParticipants ? 3000 : 500,
      category: category
    }
    let slotRoomId: any = 0

    try {
      const { data } = await selectedSlotRoomId(selectedSlot, Number(newEvent.id), newEvent.eventType, input, adminBooking)
      if (data) {
        if (data.selectedSlotRoomId.statusCode === 200) {
          slotRoomId = data.selectedSlotRoomId.data.slotRoomId
        }
      }
    } catch (error) {
      console.log('Error getting selected slot room id: ' + error)
      setErrorUseEvent('Error getting selected slot room id: ' + error)
    }
    return slotRoomId
  }


  // Cancel event
  const handleCancelEvent = async (id: number) => {
    setUpdating(true);
    try {
      const response = await cancelEvent(id);
      if (response.data.cancelEvent.statusCode === 200) {
        // Update local events state
        // setEvents(prevEvents =>
        //   prevEvents.map(event =>
        //     event.id === id
        //       ? { ...event, status: 'CANCELLED' }
        //       : event
        //   )
        // );
        resetCache(id)
        return true;
      }
      return false;
    } catch (error) {
      setErrorUseEvent('Error cancelling event');
      return false;
    } finally {
      setUpdating(false);
    }
  };

  // Revert event
  const handleRevertEvent = async (id: number) => {
    setUpdating(true)
    setErrorUseEvent(null)
    try {
      const resultsRevertVersion = await revertVersion(id)
      if (resultsRevertVersion.data.revertVersion.statusCode !== 200) throw new Error(resultsRevertVersion.data.revertVersion.message)

      resetCache(id)
      return true;
    } catch (error) {
      console.error('Error reverting event:', error);
      setErrorUseEvent('Error reverting event: ' + error);
      return false;
    } finally {
      setUpdating(false)
    }
  }

  const handleRejectEvent = async (eventId: number, rejectMessage: string = 'reject') => {
    setUpdating(true)
    setErrorUseEvent(null)
    try {
      // Get the event data to check the type
      const eventData = eventCache[eventId] || await getEventData(eventId)
      if (!eventData) throw new Error('Event not found')

      // Reject based on event type
      let rejectReturn = false
      if (eventData.eventType === EventType.SERIES) {
        rejectReturn = await rejectSeries(eventId)
      } else {
        rejectReturn = await handleSendNotification(eventId, rejectMessage)
      }
      
      if (rejectReturn) {
        resetCache(eventId)
        return true;
      } else {
        throw new Error('Error rejecting event')
      }
    } catch (error) {
      console.error('Error rejecting event:', error);
      setErrorUseEvent('Error rejecting event: ' + error);
      return false;
    } finally {
      setUpdating(false)
    }
  }

  // Send notification about event
  const handleSendNotification = async (id: number, requestType: string) => {
    setUpdating(true)
    try {
      // TODO: need to add the log message to the notification
      // TODO: need to show failure message if the notification is not sent
      await sendNotification(
        id,
        requestType === 'Custom' ? logMessage : requestType
      );
      resetCache(id)
      return true;
    } catch (error) {
      setErrorUseEvent('Error sending notification');
      return false;
    } finally {
      setUpdating(false)
    }
  };

  const rejectSeries = async (id: number) => {
    setUpdating(true)
    try {
      const seriesEvents = await getAllSeriesEvents(id)
      for (const event of seriesEvents) {
        const success = await handleSendNotification(event.id, 'reject')
        if (!success) {
          throw new Error("Error rejecting series event: " + event.id);
        }
      }
      return true;
    } catch (error) {
      setErrorUseEvent('Error in rejectSeries: ' + error);
      return false;
    } finally {
      setUpdating(false)
    }
  }

  const handleDeleteEvent = async (eventId: number) => {
    setUpdating(true);
    try {
      if (eventId > 0) {
        const response = await deleteEvent(eventId);
        if (response?.data?.deleteEvent?.statusCode === 200) {
          // Update local events state
          // setEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
          resetCache(eventId)
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error deleting event:', error);
      setErrorUseEvent('Error deleting event');
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const approveEvent = async (id: number) => {
    setUpdating(true)
    try {
      const response = await approveEventService(id)
      console.log("response", response)
      if (response.data.approveEvent.statusCode === 200) {
        resetCache(id)
        return true
      } else {
        throw new Error(response.data.approveEvent.message);
      }
    } catch (error) {
      console.log("error", error)
      setErrorUseEvent('Error approving event: ' + error);
      return false;
    } finally {
      setUpdating(false)
    }
  }

  const approveSeriesEvent = async (id: number) => {
    setUpdating(true)
    try {
      const seriesEvents = await getAllSeriesEvents(id)
      console.log("seriesEvents", seriesEvents)
      for (const event of seriesEvents) {
        const success = await approveEvent(event.id)
        if (!success) {
          throw new Error("Error approving series event: " + event.id);
        }
      }
      return true;
    } catch (error) {
      console.log("error", error)
      setErrorUseEvent('Error in approveSeriesEvent: ' + error);
      return false;
    } finally {
      setUpdating(false)
    }
  }

  const getDurationOptions = (eventType: EventType) => {
    if (eventType === EventType.WEBINAR) {
      return [15, 30, 45, 60];
    } else if (eventType === EventType.WEBCAST) {
      return [15, 30, 45, 60];
    } else if (eventType === EventType.PODCAST) {
      return [15, 30, 45, 60];
    } else if (eventType === EventType.BLOCKED) {
      return [15, 30, 45, 60];
    }
    return [15, 30, 45, 60];
  }

  const getAllSeriesEvents = async (eventId: number) => {
    const { data } = await seriesEvents(eventId)
    if (data) {
      if (data.seriesEvents.statusCode === 200) {
        data.seriesEvents.data.map((value: any) => {
          getEventData(value.id)
        })
        return data.seriesEvents.data
      } else {
        setErrorUseEvent('Error fetching series events: ' + data.seriesEvents.message)
        return []
      }
    }
    setErrorUseEvent('Error with getSeriesEvents: ' + data.seriesEvents.message)
    return []
  }

  const handleUnlockEvent = async (id: number) => {
    setUpdating(true)
    try {
      await unlockEvent(id)
      resetCache(id)
      return true;
    } catch (error) {
      console.error('Error unlocking event:', error);
      setErrorUseEvent('Error unlocking event: ' + error);
      return false;
    } finally {
      setUpdating(false)
    }
  }

  // Hand an event over to a different requester. Returns the server message so
  // the caller can surface why a transfer was refused.
  const handleTransferEventRequester = async (id: number, newRequesterId: number) => {
    setUpdating(true)
    setErrorUseEvent(null)
    try {
      const response = await transferEventRequester(id, newRequesterId)
      const { statusCode, message } = response.data.transferEventRequester
      if (statusCode !== 200) {
        setErrorUseEvent(message)
        return { success: false, message }
      }

      resetCache(id)
      return { success: true, message }
    } catch (error) {
      console.error('Error transferring event:', error);
      const message = 'Error transferring event: ' + error
      setErrorUseEvent(message);
      return { success: false, message }
    } finally {
      setUpdating(false)
    }
  }

  const handleApproveEvent = async (eventId: number) => {
    setUpdating(true)
    setErrorUseEvent(null)
    try {
      // Get the event data to check the type
      const eventData = eventCache[eventId] || await getEventData(eventId)
      if (!eventData) throw new Error('Event not found')

      // Approve based on event type
      let approveReturn = false
      if (eventData.eventType === EventType.SERIES) {
        approveReturn = await approveSeriesEvent(eventId)
      } else {
        approveReturn = await approveEvent(eventId)
      }
      
      if (approveReturn) {
        resetCache(eventId)
        return true;
      } else {
        throw new Error('Error approving event')
      }
    } catch (error) {
      console.error('Error approving event:', error);
      setErrorUseEvent('Error approving event: ' + error);
      return false;
    } finally {
      setUpdating(false)
    }
  }

  return {
    errorUseEvent,
    logMessage,
    setLogMessage,
    updating,
    eventData,
    setEventData,
    emptyEventData,
    handleCancelEvent,
    handleRevertEvent,
    handleRejectEvent,
    handleSendNotification,
    rejectSeries,
    handleDeleteEvent,
    getEventData,
    resetCache,
    getEventRoomBookingFormatted,
    getEventCreatorDetails,
    createDbEvent,
    updateDbEvent,
    initSubmitEvent,
    updateDbEventType,
    updateDbEventInfo,
    updateDbUpdateStaff,
    updateDbUpdateFeature,
    updateDbUpdateAudience,
    updateDbBooking,
    getDurationOptions,
    approveEvent,
    approveSeriesEvent,
    getAllSeriesEvents,
    handleUnlockEvent,
    handleApproveEvent,
    handleTransferEventRequester
  };
}; 